const CHARACTER_SUFFIX = 'chars';
const HEIGHT_SUFFIX = 'px';

class ReadMoreText extends HTMLElement {
  #content;
  #button;
  #fullMarkup = '';
  #collapsedMarkup = '';
  #expanded = false;
  #lastWidth = 0;
  #resizeObserver;

  connectedCallback() {
    this.#content = this.querySelector('[data-read-more-content]');
    this.#button = this.querySelector('[data-read-more-button]');

    if (!(this.#content instanceof HTMLElement) || !(this.#button instanceof HTMLButtonElement)) return;

    this.#fullMarkup = this.#content.innerHTML;
    this.#button.addEventListener('click', this.#toggle);
    this.#prepareCollapsedContent();

    if (this.dataset.collapsedSize?.endsWith(HEIGHT_SUFFIX)) {
      this.#lastWidth = this.#content.offsetWidth;
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this);
      document.fonts?.ready.then(() => this.#recalculateCollapsedContent());
    }
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#toggle);
    this.#resizeObserver?.disconnect();
  }

  #toggle = () => {
    this.#expanded = !this.#expanded;
    this.#content.innerHTML = this.#expanded ? this.#fullMarkup : this.#collapsedMarkup;
    this.dataset.expanded = String(this.#expanded);
    this.#button.setAttribute('aria-expanded', String(this.#expanded));
  };

  #handleResize = () => {
    const width = this.#content.offsetWidth;

    if (width === this.#lastWidth) return;

    this.#lastWidth = width;
    this.#recalculateCollapsedContent();
  };

  #recalculateCollapsedContent() {
    const wasExpanded = this.#expanded;
    this.#content.innerHTML = this.#fullMarkup;
    this.#prepareCollapsedContent();

    if (wasExpanded && !this.#button.hidden) {
      this.#expanded = true;
      this.#content.innerHTML = this.#fullMarkup;
      this.dataset.expanded = 'true';
      this.#button.setAttribute('aria-expanded', 'true');
    }
  }

  #prepareCollapsedContent() {
    const value = this.dataset.collapsedSize ?? '';
    const textNodes = this.#getTextNodes(this.#content);
    const fullText = textNodes.map((node) => node.data).join('');
    let cutoff = null;

    if (value.endsWith(CHARACTER_SUFFIX)) {
      cutoff = this.#findCharacterCutoff(fullText, Number.parseInt(value, 10));
    } else if (value.endsWith(HEIGHT_SUFFIX)) {
      cutoff = this.#findHeightCutoff(textNodes, fullText, Number.parseInt(value, 10));
    }

    if (cutoff === null || cutoff >= fullText.length) {
      this.#button.hidden = true;
      this.#collapsedMarkup = this.#fullMarkup;
      return;
    }

    this.#collapsedMarkup = this.#createCollapsedMarkup(cutoff);
    this.#content.innerHTML = this.#collapsedMarkup;
    this.#expanded = false;
    this.dataset.expanded = 'false';
    this.#button.hidden = false;
    this.#button.setAttribute('aria-expanded', 'false');
  }

  #findCharacterCutoff(text, limit) {
    if (!Number.isFinite(limit) || text.length <= limit) return null;

    const nextWhitespace = text.slice(limit).search(/\s/u);
    return nextWhitespace === -1 ? null : limit + nextWhitespace;
  }

  #findHeightCutoff(textNodes, text, limit) {
    if (!Number.isFinite(limit) || this.#content.scrollHeight <= limit) return null;

    const wordEnds = [...text.matchAll(/\S+/gu)].map((match) => match.index + match[0].length);
    const contentTop = this.#content.getBoundingClientRect().top;
    let low = 0;
    let high = wordEnds.length - 1;
    let cutoff = 0;

    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      const wordEnd = wordEnds[middle];
      const characterRect = this.#getCharacterRect(textNodes, wordEnd - 1);

      if (characterRect && characterRect.bottom - contentTop <= limit) {
        cutoff = wordEnd;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    return cutoff || wordEnds[0] || null;
  }

  #getCharacterRect(textNodes, characterIndex) {
    let consumed = 0;

    for (const node of textNodes) {
      const nextConsumed = consumed + node.length;

      if (characterIndex < nextConsumed) {
        const offset = characterIndex - consumed;
        const range = document.createRange();
        range.setStart(node, offset);
        range.setEnd(node, offset + 1);
        return range.getBoundingClientRect();
      }

      consumed = nextConsumed;
    }

    return null;
  }

  #createCollapsedMarkup(cutoff) {
    const clone = /** @type {HTMLElement} */ (this.#content.cloneNode(true));
    const textNodes = this.#getTextNodes(clone);
    let remaining = cutoff;

    for (const node of textNodes) {
      if (remaining <= 0) {
        node.remove();
        continue;
      }

      if (remaining >= node.length) {
        remaining -= node.length;
        continue;
      }

      node.data = node.data.slice(0, remaining);
      remaining = 0;
    }

    const lastVisibleTextNode = this.#getTextNodes(clone)
      .reverse()
      .find((node) => node.data.trim());

    if (lastVisibleTextNode) {
      lastVisibleTextNode.data = `${lastVisibleTextNode.data.trimEnd()}…`;
    }

    for (const element of [...clone.querySelectorAll('*')].reverse()) {
      if (!element.textContent?.trim() && !element.matches('br, img, video, iframe')) element.remove();
    }

    return clone.innerHTML;
  }

  #getTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;

    while ((node = walker.nextNode())) {
      if (node.textContent?.trim()) nodes.push(/** @type {Text} */ (node));
    }

    return nodes;
  }
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}
